/**
 * SmartBackLink — botão "Voltar" que sabe pra onde ir.
 *
 * Lógica:
 *   - User logado (tem session Supabase) → volta pra /app
 *   - User não logado → volta pra /login
 *   - Loading state inicial → renderiza placeholder (sem link, pra não piscar)
 *
 * Usado em páginas semi-públicas (/status, /termos, /privacidade) onde
 * tanto logados quanto visitantes podem chegar. Antes esses botões eram
 * hardcoded pra /login — ruim pra user logado que clicava em "Termos" no
 * footer e era jogado de volta na tela de login.
 */
import Link from "next/link";
import { useEffect, useState, ReactNode } from "react";
import { getSupabase } from "@/lib/supabase";

interface Props {
  children: ReactNode;
  className?: string;
}

export function SmartBackLink({ children, className }: Props) {
  const [target, setTarget] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getSupabase().auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setTarget(data.session ? "/app" : "/login");
    }).catch(() => {
      if (!cancelled) setTarget("/login");
    });
    return () => { cancelled = true; };
  }, []);

  // Loading: renderiza link "neutro" sem destino pra evitar pisca-pisca.
  // Se o user clicar antes do check, vai pra /app (otimista — se não
  // logado, /app redireciona pra /login mesmo via RequireAuth).
  if (target === null) {
    return (
      <Link href="/app" className={className}>
        {children}
      </Link>
    );
  }

  return (
    <Link href={target} className={className}>
      {children}
    </Link>
  );
}
